import { useCallback, useContext, useEffect, useState } from 'react';
import { uniq } from 'underscore';
import { Button } from 'react-bootstrap';
import { get, useFormContext } from 'react-hook-form';
import t from 'react-translate';
import { validateZoomHosts } from 'redux/actions/live-events';
import { useAppDispatch } from 'redux/store';
import NvTextInput, { Props } from 'shared/components/inputs/nv-text-input';
import NvTooltip from 'shared/components/nv-tooltip';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { LiveSessionFormContext } from './live-session-form';

const labelClass = 'text-small gray-3 m-0';

type ZoomHostValidatorType = Props & {
  formName: string;
  disabled: boolean;
  host?: string | string[]; // main host: string[] , alternativeHosts: string[]
  onValidHost?: (isValid: boolean) => void;
  label: string;
  tooltip?: string;
  isAlternativeHost?: boolean;
  description?: string;
};

const parseEmails = (input: string | undefined): string[] => (input ? input.split(',').map(e => e.trim()) : []);

const ZoomHostValidator = (props: ZoomHostValidatorType) => {
  const {
    formName,
    disabled,
    host: initialHostValue,
    onValidHost,
    label,
    tooltip,
    isAlternativeHost,
    description,
    ...restProps
  } = props;

  const { watch, setError, formState } = useFormContext();
  const { catalogId, showZoomHostValidator } = useContext(LiveSessionFormContext);
  const shouldValidate = showZoomHostValidator();

  const hostUserEmailIds = watch(formName); // eg: "john@company.com, peter@school.edu."

  const initialValidatedHosts = parseEmails(
    typeof initialHostValue === 'string'
      ? initialHostValue
      : initialHostValue?.join(',') || '',
  );

  const [validating, setValidating] = useState(false);
  const [validatedHosts, setValidatedHosts] = useState<string[]>(initialValidatedHosts);
  const [invalidHosts, setInvalidHosts] = useState<string[]>([]);

  const dispatch = useAppDispatch();

  const handleValidate = () => {
    setValidating(true);

    dispatch(validateZoomHosts({ catalogId, hostUserIds: hostUserEmailIds }))
      .then(({ payload }) => {
        const newInvalids = payload?.isInvalid || [];
        const newValids = payload?.isValid || [];

        if (newInvalids.length > 0) {
          setError(formName, { type: 'validate' });
          setInvalidHosts(prev => uniq([...prev, ...newInvalids]));
          let messageKey = 'HOST';
          if (isAlternativeHost) {
            messageKey = newInvalids.length > 1 ? 'MULTIPLE' : 'SINGLE';
          }
          const messageInput = newInvalids.length > 1 ? newInvalids.join(',') : newInvalids[0];
          dispatch(addAlertMessage({
            type: AlertMessageType.ERROR,
            header: t.FORM.OOPS(),
            message: t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_HOST_ERROR.ACCOUNT_LEVEL[messageKey](messageInput),
          }));
        }

        if (newValids.length > 0) {
          setValidatedHosts(prev => uniq([...prev, ...newValids]));
        }

        setValidating(false);
      })
      .catch(() => {
        setError(formName, { type: 'custom' });
        setValidating(false);
      });
  };

  const hasErrors = !!get(formState.errors, formName);
  const allValid = parseEmails(hostUserEmailIds).every(email => validatedHosts.includes(email));

  const hasInvalidHost = useCallback(() => (
    parseEmails(hostUserEmailIds).some(email => invalidHosts.includes(email))
  ), [hostUserEmailIds, invalidHosts]);

  useEffect(() => {
    onValidHost(allValid);
  }, [allValid, onValidHost]);

  return (
    <div className='mb-4'>
      <label className={labelClass}>{label}</label>
      <div className='d-flex w-100'>
        <NvTooltip text={tooltip} placement='top' enabled={!!tooltip}>
          <div className='w-100'>
            <NvTextInput
              withForm
              required={!isAlternativeHost}
              type={isAlternativeHost ? 'text' : 'email'}
              name={formName}
              autoComplete='off'
              disabled={disabled}
              ariaLabel={restProps.ariaLabel}
              placeholder={restProps.placeholder}
              id={formName}
            />
          </div>
        </NvTooltip>
        {shouldValidate && (
          <div
            css={{ minWidth: '110px' }}
            className='d-flex ml-2 align-items-center bold justify-content-center'
          >
            {allValid && parseEmails(hostUserEmailIds).length > 0 ? (
              <span className='text-success'>
                {t.LECTURE_PAGES.COMPONENTS.LIVE_VIDEO_EVENTS.MODAL.ZOOM_HOST_VALIDATED()}
              </span>
            ) : (
              <Button
                variant='primary'
                className='w-100'
                type='button'
                disabled={
                  !hostUserEmailIds
                  || validating
                  || disabled
                  || hasErrors
                  || hasInvalidHost()
                }
                onClick={handleValidate}
              >
                {validating ? t.FORM.VALIDATING() : t.FORM.VALIDATE()}
              </Button>
            )}
          </div>
        )}
      </div>
      {description && (
        <span className='label gray-3'>{description}</span>
      )}
    </div>
  );
};

export default ZoomHostValidator;
